import { MAX_QUIZ_COUNT } from '@/views/build/constant';
import { type BuildInfoProviderType } from '@/views/build/context';

import {
  type AiBuilderChatEntry,
  type AiBuilderSubmitPromptOptions,
} from '../store/types';

import { type AiBuilderChatCopy } from './aiBuilderChatCopy';
import { type AiBuilderBuilderContext } from './types';

export type AiBuilderQuestionState =
  | 'newEmpty'
  | 'hasQuestionsNotFull'
  | 'hasQuestionsFull';

export type AiBuilderWelcomeSuggestion = {
  emoji: string;
  label: string;
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type SourceEntry = Exclude<AiBuilderChatEntry, 'chat'>;

const GENERIC_SCOPE_LABELS = new Set(['기타']);

export const getAiBuilderQuestionState = ({
  action: _action,
  questionCount,
}: {
  action: BuildInfoProviderType['action'];
  questionCount: number;
}): AiBuilderQuestionState => {
  if (questionCount <= 0) {
    return 'newEmpty';
  }

  if (questionCount >= MAX_QUIZ_COUNT) {
    return 'hasQuestionsFull';
  }

  return 'hasQuestionsNotFull';
};

export const getAiBuilderScopeLabel = ({
  schoolLevel,
  grade,
  subject,
}: Pick<AiBuilderBuilderContext, 'schoolLevel' | 'grade' | 'subject'>) =>
  joinNonEmpty(
    normalizeReliableScopeLabel(schoolLevel),
    normalizeReliableScopeLabel(grade),
    normalizeReliableScopeLabel(subject),
  );

const getAiBuilderGradeSubjectScopeLabel = ({
  grade,
  subject,
}: Pick<AiBuilderBuilderContext, 'grade' | 'subject'>) =>
  joinNonEmpty(
    normalizeReliableScopeLabel(grade),
    normalizeReliableScopeLabel(subject),
  );

export const getAiBuilderQuestionStateLead = ({
  builderContext,
  copy,
  questionState,
}: {
  builderContext: AiBuilderBuilderContext;
  copy: AiBuilderChatCopy;
  questionState: AiBuilderQuestionState;
}) => {
  if (questionState === 'hasQuestionsFull') {
    return copy.intro.hasQuestionsFullLead;
  }

  if (questionState === 'hasQuestionsNotFull') {
    return copy.intro.hasQuestionsNotFullLead;
  }

  const scopeLabel = getAiBuilderScopeLabel(builderContext);
  if (!scopeLabel) {
    return copy.intro.newEmptyLead;
  }

  return copy.intro.scopedNewEmptyLead(scopeLabel);
};

export const getAiBuilderEntryGuide = (
  entryIntent: AiBuilderChatEntry,
  copy: AiBuilderChatCopy,
) => {
  switch (entryIntent) {
    case 'file':
      return copy.intro.fileGuide;
    case 'youtube':
      return copy.intro.youtubeGuide;
    case 'website':
      return copy.intro.websiteGuide;
    case 'chat':
      return copy.intro.chatGuide;
  }
};

export const getAiBuilderWelcomeSuggestions = ({
  builderContext,
  copy,
  entryIntent,
  questionState,
}: {
  builderContext: AiBuilderBuilderContext;
  copy: AiBuilderChatCopy;
  entryIntent: AiBuilderChatEntry;
  questionState: AiBuilderQuestionState;
}): AiBuilderWelcomeSuggestion[] => {
  if (questionState === 'hasQuestionsFull') {
    return getReviewWelcomeSuggestions(copy);
  }

  if (entryIntent !== 'chat') {
    return getSourceWelcomeSuggestions(entryIntent, questionState, copy);
  }

  if (questionState === 'hasQuestionsNotFull') {
    return [
      ...getReviewWelcomeSuggestions(copy).slice(0, 2),
      { emoji: '➕', label: copy.welcome.addMoreQuestions },
    ];
  }

  return getCreateWelcomeSuggestions(builderContext, copy);
};

const getCreateWelcomeSuggestions = (
  builderContext: AiBuilderBuilderContext,
  copy: AiBuilderChatCopy,
): AiBuilderWelcomeSuggestion[] => {
  const scopeLabel = getAiBuilderScopeLabel(builderContext);
  const mixedScopeLabel = getAiBuilderGradeSubjectScopeLabel(builderContext);

  return [
    {
      emoji: '✨',
      label: scopeLabel
        ? copy.welcome.createTenScopedQuestions(scopeLabel)
        : copy.welcome.createTenQuestions,
      turnHints: {
        nextClarification: 'topic',
      },
    },
    {
      emoji: '🔀',
      label: mixedScopeLabel
        ? copy.welcome.createMixedScopedQuestions(mixedScopeLabel)
        : copy.welcome.createMixedQuestions,
      turnHints: {
        quizTypes: ['ox', 'multiple', 'subjective'],
        nextClarification: 'topic',
      },
    },
    {
      emoji: '🌱',
      label: getEasyQuestionPrompt(builderContext, copy),
      turnHints: {
        difficulty: 1,
        nextClarification: 'topic',
      },
    },
  ];
};

const getReviewWelcomeSuggestions = (
  copy: AiBuilderChatCopy,
): AiBuilderWelcomeSuggestion[] => [
  { emoji: '🔎', label: copy.welcome.reviewAllQuestions },
  { emoji: '✏️', label: copy.welcome.polishQuestionAndOptions },
  { emoji: '🎯', label: copy.welcome.checkDifficultyBalance },
];

const getSourceWelcomeSuggestions = (
  entryIntent: SourceEntry,
  questionState: AiBuilderQuestionState,
  copy: AiBuilderChatCopy,
): AiBuilderWelcomeSuggestion[] => {
  const prompts = copy.welcome.sourcePrompts[entryIntent];

  if (questionState === 'hasQuestionsNotFull') {
    return [
      { emoji: '➕', label: prompts.addFromSource },
      { emoji: '💡', label: prompts.extractKeyConcepts },
      {
        emoji: '🌱',
        label: prompts.createEasyFromSource,
        turnHints: {
          difficulty: 1,
        },
      },
    ];
  }

  return [
    {
      emoji: getSourceEntryEmoji(entryIntent),
      label: prompts.createFromSource,
    },
    { emoji: '💡', label: prompts.extractKeyConcepts },
    {
      emoji: '🌱',
      label: prompts.createEasyFromSource,
      turnHints: {
        difficulty: 1,
      },
    },
  ];
};

const getSourceEntryEmoji = (entryIntent: SourceEntry) => {
  switch (entryIntent) {
    case 'file':
      return '📄';
    case 'youtube':
      return '▶️';
    case 'website':
      return '🌐';
  }
};

const getEasyQuestionPrompt = (
  { grade, subject }: Pick<AiBuilderBuilderContext, 'grade' | 'subject'>,
  copy: AiBuilderChatCopy,
) => {
  const reliableGrade = normalizeReliableScopeLabel(grade);
  const reliableSubject = normalizeReliableScopeLabel(subject);
  const subjectPart = reliableSubject ? ` ${reliableSubject}` : '';

  if (reliableGrade) {
    return copy.welcome.createEasyForGrade({
      grade: reliableGrade,
      subjectPart,
    });
  }

  if (reliableSubject) {
    return copy.welcome.createEasyForSubject(reliableSubject);
  }

  return copy.welcome.createEasyQuestions;
};

const normalizeReliableScopeLabel = (label: string) => {
  const trimmedLabel = label.trim();
  if (!trimmedLabel || GENERIC_SCOPE_LABELS.has(trimmedLabel)) return '';

  return trimmedLabel;
};

const joinNonEmpty = (...parts: string[]) =>
  parts
    .map(part => part.trim())
    .filter(Boolean)
    .join(' ');
